import { supabase } from "@/integrations/supabase/client";

/**
 * Shared helper for invoking Supabase edge functions with a uniform error
 * shape. Every page that calls `supabase.functions.invoke(...)` should go
 * through this helper so users see the same wording on failure and so that
 * Sentry/logs see one error class to group on.
 *
 * Why: BUG-02 / BUG-05 in the 2026-04-19 QA report · different callers showed
 * cryptic raw FunctionsError messages ("non-2xx") or silent failures. This
 * normalizes the error surface and attaches the function name for triage.
 */

/**
 * Wrap a promise in a hard timeout. supabase.functions.invoke does not
 * surface a timeout when the upstream edge function hangs · the call
 * just sits there forever and the user sees a progress bar at 94%.
 * Use this when an edge function has known upstream dependencies (FMCSA,
 * QCMobile) that can stall.
 */
export async function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  timeoutMessage = "The lookup is taking longer than expected. Please try again.",
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const timeoutPromise = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => reject(new Error(timeoutMessage)), timeoutMs);
  });
  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * Pull the real error message out of a `supabase.functions.invoke` failure.
 *
 * supabase-js wraps every non-2xx response as a FunctionsHttpError whose
 * `.message` is always "Edge Function returned a non-2xx status code" — useless
 * to surface to a user. The original Response (with whatever JSON body the edge
 * function actually returned) is hanging off the `.context` property. Clone
 * and parse it so callers see "No carrier found for MC#12345" or "FMCSA API
 * key not configured" instead of the transport string.
 *
 * Use this in any catch block for a user-facing lookup / mutation flow.
 */
export async function unwrapEdgeError(
  fnError: unknown,
  fallback = "Something went wrong. Please try again.",
): Promise<string> {
  const ctx = (fnError as { context?: Response } | null)?.context;
  if (ctx && typeof (ctx as Response).clone === "function") {
    try {
      const raw = await (ctx as Response).clone().text();
      const parsed = raw ? JSON.parse(raw) : null;
      const msg =
        (parsed && typeof parsed.error === "string" && parsed.error.trim()) ||
        (parsed && typeof parsed.message === "string" && parsed.message.trim()) ||
        null;
      if (msg) return msg;
    } catch {
      // body wasn't JSON — fall through
    }
  }
  if (fnError instanceof Error) {
    const m = fnError.message?.trim();
    if (m && !/non-2xx status code/i.test(m)) return m;
  }
  return fallback;
}

export class EdgeFunctionError extends Error {
  public readonly functionName: string;
  /** Safe-to-show-a-user message. Defaults to a friendly fallback. */
  public readonly userMessage: string;
  /** Raw transport-level error if any (for Sentry / logs, not UI). */
  public readonly cause?: unknown;

  constructor(
    functionName: string,
    userMessage: string,
    technicalMessage?: string,
    cause?: unknown,
  ) {
    super(technicalMessage ?? userMessage);
    this.name = "EdgeFunctionError";
    this.functionName = functionName;
    this.userMessage = userMessage;
    this.cause = cause;
  }
}

function extractUserMessage(err: unknown, fallback: string): string {
  if (!err) return fallback;
  if (err instanceof Error) {
    const msg = err.message?.trim();
    // Strip the generic supabase envelope so we don't show "Edge Function
    // returned a non-2xx status code" · it means nothing to a dispatcher.
    if (!msg || /non-2xx status code/i.test(msg)) return fallback;
    return msg;
  }
  if (typeof err === "string" && err.trim()) return err;
  return fallback;
}

interface EdgeErrorBody {
  error?: unknown;
  message?: unknown;
  code?: unknown;
  request_id?: unknown;
}

/**
 * supabase-js throws a generic FunctionsHttpError for any non-2xx response.
 * The original Response (with the JSON body our edge functions actually
 * return) is on the `context` property. Clone-and-parse it so callers see
 * the real reason ("FMCSA API key not configured", "No carrier found for
 * MC#12345", etc.) instead of the transport string.
 */
async function readErrorBody(err: unknown): Promise<EdgeErrorBody | null> {
  const ctx = (err as { context?: unknown } | null)?.context;
  if (!ctx || typeof (ctx as Response).clone !== "function") return null;
  try {
    return (await (ctx as Response).clone().json()) as EdgeErrorBody;
  } catch {
    return null;
  }
}

function pickBodyMessage(body: EdgeErrorBody | null): string | null {
  if (!body) return null;
  const msg =
    (typeof body.error === "string" && body.error.trim()) ||
    (typeof body.message === "string" && body.message.trim());
  return msg || null;
}

/**
 * Classify a failed verification call into one of two user-facing buckets:
 *
 *   `not_recognized` · the input didn't match anything (bad number,
 *                       carrier not in registry, 404). Tells the user to
 *                       double-check what they typed.
 *   `backend_error`  · upstream / our service blew up (FMCSA outage,
 *                       timeout, 5xx, scraper blocked). Tells the user to
 *                       try again later.
 *
 * Why two buckets: showing "Lookup failed · 502" or the raw transport
 * string forces a dispatcher to guess whether they typed the wrong digits
 * or whether FMCSA is just down. One canonical message per case removes
 * that guesswork.
 */
export type VerificationFailureKind = "not_recognized" | "backend_error";

export interface VerificationFailure {
  kind: VerificationFailureKind;
  /** Safe-to-show-a-user message. */
  message: string;
  /** Edge-function error code, when one was returned. For analytics / Sentry. */
  code: string | null;
}

export interface ClassifyVerificationOptions {
  /** Noun for the not-recognized default message (e.g. "carrier", "broker", "MC number"). */
  subject?: string;
  /** Override the not-recognized message entirely. */
  notRecognizedMessage?: string;
  /** Override the backend-error message entirely. */
  backendErrorMessage?: string;
}

const NOT_RECOGNIZED_CODES = new Set([
  "not_found",
  "no_match",
  "carrier_not_found",
  "fmcsa_not_found",
  "broker_not_found",
  "cab_id_not_found",
  "bc_not_on_bulletin",
  "invalid_identifier",
  "query_too_short",
]);

const BACKEND_ERROR_CODES = new Set([
  "fmcsa_unavailable",
  "fmcsa_error",
  "ca_lookup_failed",
  "scraper_blocked",
  "scraper_timeout",
  "scraper_parse_error",
  "service_misconfiguration",
  "no_verification_path",
  "no_verification_data",
  "rate_limited",
  "unauthorized",
]);

export async function classifyVerificationError(
  err: unknown,
  options: ClassifyVerificationOptions = {},
): Promise<VerificationFailure> {
  const subject = options.subject ?? "carrier";
  const notRecognizedMessage =
    options.notRecognizedMessage ??
    `We couldn't find a ${subject} matching that number. Double-check it and try again.`;
  const backendErrorMessage =
    options.backendErrorMessage ??
    "Verification is temporarily unavailable. Please try again in a moment.";

  const body = await readErrorBody(err);
  // PREFER body.code (machine identifier like "not_found", "signup_required")
  // over body.error (human message). Old order picked `error` first, which is
  // a sentence — so downstream switches on `code` never matched. Bug found
  // 2026-05-12 when DOT lookups were showing generic "Something went wrong"
  // for clean 404 / 400 / 403 responses with valid code fields.
  const code =
    (body && typeof body.code === "string" && body.code.trim()) ||
    (body && typeof body.error === "string" && body.error.trim()) ||
    null;

  if (code && NOT_RECOGNIZED_CODES.has(code)) {
    return { kind: "not_recognized", message: notRecognizedMessage, code };
  }
  if (code && BACKEND_ERROR_CODES.has(code)) {
    return { kind: "backend_error", message: backendErrorMessage, code };
  }

  // Fall back to pattern-matching the transport / message string. Covers
  // network failures, hard timeouts, and edge functions that emit a 404 /
  // 5xx without one of the known codes.
  const raw =
    err instanceof Error
      ? err.message ?? ""
      : typeof err === "string"
        ? err
        : "";
  if (/\b404\b/.test(raw) || /not[\s_-]?found/i.test(raw)) {
    return { kind: "not_recognized", message: notRecognizedMessage, code };
  }
  if (
    /\b5\d\d\b/.test(raw) ||
    /timeout|timed out|ECONN|fetch failed|network/i.test(raw) ||
    /unavailable|FunctionsFetchError/i.test(raw)
  ) {
    return { kind: "backend_error", message: backendErrorMessage, code };
  }

  // Unknown failure · default to backend error rather than implying the
  // user typed a bad number when we don't actually know what broke.
  return { kind: "backend_error", message: backendErrorMessage, code };
}

export async function callEdgeFunction<TResponse = unknown>(
  functionName: string,
  body?: Record<string, unknown>,
  options?: {
    /** Shown if the function call fails with no useful message. */
    fallbackMessage?: string;
  },
): Promise<TResponse> {
  const fallback =
    options?.fallbackMessage ??
    "We couldn't reach the server. Please try again in a moment.";
  try {
    const { data, error } = await supabase.functions.invoke(functionName, {
      body,
    });
    if (error) {
      // Pull the real error body off the wrapped Response before falling
      // back to the generic "non-2xx" string supabase-js attached to .message.
      const errorBody = await readErrorBody(error);
      const bodyMsg = pickBodyMessage(errorBody);
      const userMessage = bodyMsg ?? extractUserMessage(error, fallback);
      throw new EdgeFunctionError(
        functionName,
        userMessage,
        `[${functionName}] ${error.message ?? "edge function error"}${
          errorBody?.code ? ` · code=${String(errorBody.code)}` : ""
        }${errorBody?.request_id ? ` · request_id=${String(errorBody.request_id)}` : ""}`,
        error,
      );
    }
    // Some functions return { error: "..."} in the JSON body instead of
    // throwing. Treat that as a failure so callers don't have to re-check.
    if (data && typeof data === "object" && "error" in (data as object)) {
      const payload = data as { error?: unknown };
      if (payload.error) {
        throw new EdgeFunctionError(
          functionName,
          extractUserMessage(payload.error, fallback),
          `[${functionName}] ${String(payload.error)}`,
          payload.error,
        );
      }
    }
    return data as TResponse;
  } catch (e) {
    if (e instanceof EdgeFunctionError) throw e;
    throw new EdgeFunctionError(
      functionName,
      extractUserMessage(e, fallback),
      e instanceof Error ? `[${functionName}] ${e.message}` : undefined,
      e,
    );
  }
}
